import instance from './axios';
import { getFcmToken, onForegroundMessage } from './firebaseConfig';

let pushRegistered = false;
let fcmAvailable = false;

/**
 * Check if push is supported and what the current permission state is.
 */
export function getPushState(): 'unsupported' | 'default' | 'granted' | 'denied' {
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Check if push is already fully registered (subscribed + sent to backend).
 */
export function isPushRegistered(): boolean {
  return pushRegistered;
}

/**
 * Pre-fetch push config — check if FCM is enabled on the server.
 * Called on app load (no user gesture needed).
 */
export async function prefetchPushConfig(): Promise<boolean> {
  try {
    const { data } = await instance.get('/push/status');
    console.log('[Push] Server push status:', data);
    // Even if server says enabled=false (no credentials yet), we still allow
    // the client to register FCM tokens so they're ready when the server gets credentials.
    if (data.provider === 'fcm' || data.migrated === 'fcm') {
      fcmAvailable = true;
      return true;
    }
    // Server has FCM routes — allow registration
    if (data.enabled !== undefined) {
      fcmAvailable = true;
      return true;
    }
  } catch {
    // Backend may not have push routes yet — try FCM client-side anyway
  }
  // Default: allow FCM client-side registration regardless of server state
  fcmAvailable = true;
  return true;
}

/**
 * If permission is already granted (from a previous session), silently subscribe.
 * No user gesture needed since permission was already granted.
 */
export async function autoSubscribeIfGranted(): Promise<void> {
  if (pushRegistered || Notification.permission !== 'granted') return;
  await registerFcmToken();
}

/**
 * Request permission and subscribe. MUST be called from a user gesture (click handler).
 */
export async function requestAndSubscribe(): Promise<boolean> {
  try {
    console.log('[Push] requestAndSubscribe called');

    if (!fcmAvailable) {
      console.log('[Push] Checking push config...');
      const configured = await prefetchPushConfig();
      console.log('[Push] prefetchPushConfig result:', configured);
      if (!configured) return false;
    }

    console.log('[Push] Calling Notification.requestPermission()...');
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission result:', permission);
    if (permission !== 'granted') return false;

    console.log('[Push] Registering FCM token...');
    await registerFcmToken();
    return true;
  } catch (error) {
    console.error('[Push] requestAndSubscribe failed:', error);
    return false;
  }
}

/**
 * Internal — get FCM token and register with backend.
 */
async function registerFcmToken(): Promise<void> {
  if (pushRegistered) {
    console.log('[Push] Already registered, skipping');
    return;
  }

  try {
    console.log('[Push] Getting FCM token...');
    const token = await getFcmToken();

    if (!token) {
      console.warn('[Push] Could not get FCM token — check Firebase config');
      return;
    }

    console.log('[Push] Got FCM token, sending to backend...');
    await instance.post('/push/register-token', {
      token,
      deviceInfo: navigator.userAgent.substring(0, 200),
    });

    pushRegistered = true;
    console.log('[Push] FCM token registered successfully!');
  } catch (error) {
    console.error('[Push] FCM token registration failed:', error);
  }
}

/**
 * Legacy entry point — called from NotificationBell on auth.
 * Prefetches config and auto-subscribes if already granted.
 */
export async function initPushNotifications(): Promise<void> {
  if (pushRegistered) return;
  if (getPushState() === 'unsupported') return;

  await prefetchPushConfig();
  await autoSubscribeIfGranted();
}

/**
 * Set up foreground message handler. Returns unsubscribe function.
 */
export function setupForegroundHandler(callback: (payload: any) => void): (() => void) | null {
  return onForegroundMessage(callback);
}
